import React, { useEffect, useMemo, useState } from 'react'
import SiteSettings from './SiteSettings'
import ImagePicker from '../../components/ImagePicker'
import SafeIcon from '../../common/SafeIcon'
import supabase from '../../lib/supabase'
import * as FiIcons from 'react-icons/fi'

const {
  FiImage,
  FiX,
  FiSave,
  FiSliders,
  FiTrash2,
  FiArrowLeft,
  FiArrowRight,
  FiPlus,
  FiCheckCircle,
  FiAlertCircle,
} = FiIcons

const DEFAULTS = {
  hero_background_image: '/assets/aox-hero-team-2026.webp',
  hero_background_images: ['/assets/aox-hero-team-2026.webp'],
  hero_background_position: '58% center',
  hero_background_position_mobile: '64% center',
  hero_overlay_opacity: 10,
  hero_effects_enabled: true,
  hero_slideshow_enabled: true,
  hero_slideshow_interval: 8000,
  hero_transition_duration: 1400,
  hero_content_box_opacity: 10,
  academy_background_images: [],
  academy_slideshow_enabled: true,
  academy_slideshow_interval: 9000,
  academy_transition_duration: 1500,
  academy_overlay_opacity: 48,
  academy_background_position: 'center center',
  sponsors_background_images: [],
  sponsors_slideshow_enabled: true,
  sponsors_slideshow_interval: 9000,
  sponsors_transition_duration: 1500,
  sponsors_overlay_opacity: 62,
  sponsors_background_position: 'center center',
}

const cleanImages = (value, fallback = []) => {
  const list = Array.isArray(value)
    ? value.filter((item) => typeof item === 'string' && item.trim()).map((item) => item.trim())
    : []
  return list.length ? [...new Set(list)] : fallback
}

const clamp = (value, min, max, fallback) => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return fallback
  return Math.max(min, Math.min(max, parsed))
}

const normalize = (data = {}) => {
  const heroFallback = data.hero_background_image || DEFAULTS.hero_background_image
  return {
    ...DEFAULTS,
    ...data,
    hero_background_image: heroFallback,
    hero_background_images: cleanImages(data.hero_background_images, [heroFallback]),
    academy_background_images: cleanImages(data.academy_background_images, []),
    sponsors_background_images: cleanImages(data.sponsors_background_images, []),
    hero_overlay_opacity: clamp(data.hero_overlay_opacity, 0, 80, 10),
    hero_content_box_opacity: clamp(data.hero_content_box_opacity, 0, 60, 10),
    academy_overlay_opacity: clamp(data.academy_overlay_opacity, 0, 90, 48),
    sponsors_overlay_opacity: clamp(data.sponsors_overlay_opacity, 0, 90, 62),
    hero_slideshow_interval: clamp(data.hero_slideshow_interval, 3000, 30000, 8000),
    academy_slideshow_interval: clamp(data.academy_slideshow_interval, 3000, 30000, 9000),
    sponsors_slideshow_interval: clamp(data.sponsors_slideshow_interval, 3000, 30000, 9000),
    hero_transition_duration: clamp(data.hero_transition_duration, 300, 5000, 1400),
    academy_transition_duration: clamp(data.academy_transition_duration, 300, 5000, 1500),
    sponsors_transition_duration: clamp(data.sponsors_transition_duration, 300, 5000, 1500),
  }
}

const Toggle = ({ checked, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={`relative h-8 w-14 rounded-full transition ${checked ? 'bg-blue-600' : 'bg-slate-300'}`}
    aria-pressed={checked}
  >
    <span
      className={`absolute top-1 h-6 w-6 rounded-full bg-white shadow transition-transform ${
        checked ? 'translate-x-7' : 'translate-x-1'
      }`}
    />
  </button>
)

const RangeControl = ({ label, value, min, max, step = 1, suffix = '', onChange, help }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4">
    <div className="mb-3 flex items-center justify-between gap-3">
      <label className="text-sm font-bold text-slate-800">{label}</label>
      <span className="rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-black text-slate-700">
        {value}{suffix}
      </span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full accent-blue-600"
    />
    {help && <p className="mt-2 text-xs leading-relaxed text-slate-500">{help}</p>}
  </div>
)

const ImageListEditor = ({ title, description, images, onChange, folder }) => {
  const [url, setUrl] = useState('')
  const list = cleanImages(images, [])

  const addImage = (imageUrl) => {
    const clean = String(imageUrl || '').trim()
    if (!clean || list.includes(clean)) return
    onChange([...list, clean])
    setUrl('')
  }

  const removeImage = (index) => onChange(list.filter((_, itemIndex) => itemIndex !== index))

  const moveImage = (index, direction) => {
    const target = index + direction
    if (target < 0 || target >= list.length) return
    const next = [...list]
    ;[next[index], next[target]] = [next[target], next[index]]
    onChange(next)
  }

  return (
    <section className="rounded-3xl border border-slate-200 bg-slate-50 p-5 md:p-6">
      <div className="mb-4">
        <h3 className="text-lg font-extrabold text-slate-950">{title}</h3>
        <p className="mt-1 text-sm text-slate-500">{description}</p>
      </div>

      {list.length > 0 ? (
        <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((image, index) => (
            <div key={`${image}-${index}`} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div
                className="aspect-video bg-slate-200 bg-cover bg-center"
                style={{ backgroundImage: `url(${JSON.stringify(image)})` }}
              />
              <div className="flex items-center justify-between gap-2 p-3">
                <span className="min-w-0 truncate text-xs font-bold text-slate-600">#{index + 1}</span>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => moveImage(index, -1)}
                    disabled={index === 0}
                    className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-25"
                    title="Μετακίνηση αριστερά"
                  >
                    <SafeIcon icon={FiArrowLeft} className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveImage(index, 1)}
                    disabled={index === list.length - 1}
                    className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-25"
                    title="Μετακίνηση δεξιά"
                  >
                    <SafeIcon icon={FiArrowRight} className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="rounded-lg p-2 text-red-500 hover:bg-red-50"
                    title="Διαγραφή"
                  >
                    <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mb-5 rounded-2xl border border-dashed border-slate-300 bg-white px-5 py-8 text-center text-sm text-slate-500">
          Δεν υπάρχουν ακόμα background images. Το section θα κρατήσει το υπάρχον gradient.
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <div>
          <div className="mb-2 text-sm font-bold text-slate-700">Upload / επιλογή εικόνας</div>
          <ImagePicker
            value=""
            onChange={addImage}
            folder={folder}
            bucket="team_logos"
            aspectRatio="aspect-video"
          />
        </div>

        <div>
          <div className="mb-2 text-sm font-bold text-slate-700">Προσθήκη με URL / path</div>
          <div className="flex gap-2">
            <input
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  addImage(url)
                }
              }}
              className="min-w-0 flex-1 rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              placeholder="https://... ή /assets/..."
            />
            <button
              type="button"
              onClick={() => addImage(url)}
              className="flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-bold text-white hover:bg-blue-800"
            >
              <SafeIcon icon={FiPlus} className="h-4 w-4" />
              Προσθήκη
            </button>
          </div>
          <p className="mt-2 text-xs text-slate-500">Η σειρά των καρτών είναι και η σειρά του slideshow.</p>
        </div>
      </div>
    </section>
  )
}

const PositionSelect = ({ label, value, onChange }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4">
    <label className="mb-2 block text-sm font-bold text-slate-800">{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
    >
      <option value="center center">Κέντρο</option>
      <option value="58% center">58% / κέντρο</option>
      <option value="64% center">64% / κέντρο</option>
      <option value="left center">Αριστερά</option>
      <option value="right center">Δεξιά</option>
      <option value="center top">Κέντρο / επάνω</option>
      <option value="center bottom">Κέντρο / κάτω</option>
    </select>
  </div>
)

const SlideshowControls = ({ enabled, onEnabled, interval, onInterval, transition, onTransition }) => (
  <section className="grid gap-4 md:grid-cols-3">
    <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-4">
      <div className="pr-4">
        <div className="text-sm font-bold text-slate-800">Auto slideshow</div>
        <div className="mt-1 text-xs text-slate-500">Αυτόματη αλλαγή background.</div>
      </div>
      <Toggle checked={enabled} onChange={onEnabled} />
    </div>
    <RangeControl
      label="Αλλαγή κάθε"
      value={Math.round(interval / 1000)}
      min={3}
      max={30}
      suffix=" sec"
      onChange={(value) => onInterval(value * 1000)}
    />
    <RangeControl
      label="Fade duration"
      value={(transition / 1000).toFixed(1)}
      min={0.3}
      max={5}
      step={0.1}
      suffix=" sec"
      onChange={(value) => onTransition(Math.round(value * 1000))}
    />
  </section>
)

const SiteSettingsBackgrounds = () => {
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState('hero')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [configId, setConfigId] = useState(null)
  const [form, setForm] = useState(DEFAULTS)
  const [message, setMessage] = useState(null)

  const loadSettings = async () => {
    setLoading(true)
    setMessage(null)
    const { data, error } = await supabase
      .from('site_config_aox1234')
      .select(
        'id,hero_background_image,hero_background_images,hero_background_position,hero_background_position_mobile,hero_overlay_opacity,hero_effects_enabled,hero_slideshow_enabled,hero_slideshow_interval,hero_transition_duration,hero_content_box_opacity,academy_background_images,academy_slideshow_enabled,academy_slideshow_interval,academy_transition_duration,academy_overlay_opacity,academy_background_position,sponsors_background_images,sponsors_slideshow_enabled,sponsors_slideshow_interval,sponsors_transition_duration,sponsors_overlay_opacity,sponsors_background_position',
      )
      .limit(1)
      .single()

    if (error) {
      console.error('Homepage background settings load error:', error)
      setMessage({ type: 'error', text: 'Δεν ήταν δυνατή η φόρτωση των background settings.' })
    } else {
      setConfigId(data.id)
      setForm(normalize(data))
    }
    setLoading(false)
  }

  useEffect(() => {
    loadSettings()
  }, [])

  const update = (name, value) => setForm((prev) => ({ ...prev, [name]: value }))

  const payload = useMemo(() => {
    const normalized = normalize(form)
    return {
      ...normalized,
      hero_background_image:
        normalized.hero_background_images[0] || normalized.hero_background_image || DEFAULTS.hero_background_image,
    }
  }, [form])

  const save = async () => {
    if (!configId) return
    setSaving(true)
    setMessage(null)

    const { error } = await supabase
      .from('site_config_aox1234')
      .update({ ...payload, updated_at: new Date().toISOString() })
      .eq('id', configId)

    if (error) {
      console.error('Homepage background settings save error:', error)
      setMessage({ type: 'error', text: 'Αποτυχία αποθήκευσης των background settings.' })
    } else {
      setForm(payload)
      setMessage({ type: 'success', text: 'Οι background ρυθμίσεις αποθηκεύτηκαν.' })
    }
    setSaving(false)
  }

  const tabs = [
    { id: 'hero', label: 'Hero' },
    { id: 'academy', label: 'Academy' },
    { id: 'sponsors', label: 'Sponsors' },
  ]

  return (
    <>
      <SiteSettings />

      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-40 flex items-center gap-3 rounded-2xl bg-slate-950 px-5 py-3.5 text-sm font-bold text-white shadow-2xl ring-1 ring-white/10 transition hover:-translate-y-0.5 hover:bg-blue-900"
      >
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-500/20 text-blue-200">
          <SafeIcon icon={FiImage} className="h-5 w-5" />
        </span>
        <span className="hidden sm:block">Backgrounds</span>
        <SafeIcon icon={FiSliders} className="h-4 w-4 text-blue-200" />
      </button>

      {open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm">
          <div className="max-h-[95vh] w-full max-w-6xl overflow-y-auto rounded-3xl bg-white shadow-2xl">
            <div className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 px-6 py-5 backdrop-blur md:px-8">
              <div className="flex items-start justify-between gap-5">
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.18em] text-blue-600">Admin → Site Settings</div>
                  <h2 className="mt-1 text-2xl font-black text-slate-950">Homepage Backgrounds</h2>
                  <p className="mt-1 text-sm text-slate-500">Hero, Academy και Sponsors με αυτόματο cross-fade slideshow.</p>
                </div>
                <button type="button" onClick={() => setOpen(false)} className="rounded-xl p-2.5 text-slate-500 hover:bg-slate-100">
                  <SafeIcon icon={FiX} className="h-6 w-6" />
                </button>
              </div>

              <div className="mt-5 flex flex-wrap gap-2">
                {tabs.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setTab(item.id)}
                    className={`rounded-xl px-4 py-2 text-sm font-bold transition ${
                      tab === item.id ? 'bg-blue-700 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-5 p-6 md:p-8">
              {message && (
                <div className={`flex items-center gap-3 rounded-2xl border px-4 py-3 text-sm font-medium ${
                  message.type === 'success'
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                    : 'border-red-200 bg-red-50 text-red-800'
                }`}>
                  <SafeIcon icon={message.type === 'success' ? FiCheckCircle : FiAlertCircle} className="h-5 w-5" />
                  {message.text}
                </div>
              )}

              {loading ? (
                <div className="flex min-h-64 items-center justify-center">
                  <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600" />
                </div>
              ) : (
                <>
                  {tab === 'hero' && (
                    <>
                      <ImageListEditor
                        title="Hero Background Images"
                        description="Πρόσθεσε μία ή περισσότερες εικόνες. Η πρώτη είναι και το fallback/default Hero image."
                        images={form.hero_background_images}
                        onChange={(value) => update('hero_background_images', value)}
                        folder="hero"
                      />
                      <SlideshowControls
                        enabled={form.hero_slideshow_enabled}
                        onEnabled={(value) => update('hero_slideshow_enabled', value)}
                        interval={form.hero_slideshow_interval}
                        onInterval={(value) => update('hero_slideshow_interval', value)}
                        transition={form.hero_transition_duration}
                        onTransition={(value) => update('hero_transition_duration', value)}
                      />
                      <section className="grid gap-4 md:grid-cols-2">
                        <RangeControl
                          label="Hero dark overlay"
                          value={form.hero_overlay_opacity}
                          min={0}
                          max={80}
                          suffix="%"
                          onChange={(value) => update('hero_overlay_opacity', value)}
                        />
                        <RangeControl
                          label="Content box opacity"
                          value={form.hero_content_box_opacity}
                          min={0}
                          max={60}
                          suffix="%"
                          onChange={(value) => update('hero_content_box_opacity', value)}
                          help="0% = τελείως διάφανο box. Μικρότερη τιμή αφήνει τις παίκτριες να φαίνονται περισσότερο."
                        />
                      </section>
                      <section className="grid gap-4 md:grid-cols-3">
                        <PositionSelect label="Desktop position" value={form.hero_background_position} onChange={(value) => update('hero_background_position', value)} />
                        <PositionSelect label="Mobile position" value={form.hero_background_position_mobile} onChange={(value) => update('hero_background_position_mobile', value)} />
                        <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-4">
                          <div className="pr-4">
                            <div className="text-sm font-bold text-slate-800">Grid & light effects</div>
                            <div className="mt-1 text-xs text-slate-500">Τα υπάρχοντα stadium effects.</div>
                          </div>
                          <Toggle checked={form.hero_effects_enabled} onChange={(value) => update('hero_effects_enabled', value)} />
                        </div>
                      </section>
                    </>
                  )}

                  {tab === 'academy' && (
                    <>
                      <ImageListEditor
                        title="Academy Background Images"
                        description="Αν αφήσεις τη λίστα κενή, παραμένει το σημερινό μπλε gradient της Academy ενότητας."
                        images={form.academy_background_images}
                        onChange={(value) => update('academy_background_images', value)}
                        folder="academy-backgrounds"
                      />
                      <SlideshowControls
                        enabled={form.academy_slideshow_enabled}
                        onEnabled={(value) => update('academy_slideshow_enabled', value)}
                        interval={form.academy_slideshow_interval}
                        onInterval={(value) => update('academy_slideshow_interval', value)}
                        transition={form.academy_transition_duration}
                        onTransition={(value) => update('academy_transition_duration', value)}
                      />
                      <section className="grid gap-4 md:grid-cols-2">
                        <RangeControl
                          label="Academy dark overlay"
                          value={form.academy_overlay_opacity}
                          min={0}
                          max={90}
                          suffix="%"
                          onChange={(value) => update('academy_overlay_opacity', value)}
                        />
                        <PositionSelect label="Background position" value={form.academy_background_position} onChange={(value) => update('academy_background_position', value)} />
                      </section>
                    </>
                  )}

                  {tab === 'sponsors' && (
                    <>
                      <ImageListEditor
                        title="Sponsors Background Images"
                        description="Αν αφήσεις τη λίστα κενή, παραμένει το σημερινό dark-blue gradient των χορηγών."
                        images={form.sponsors_background_images}
                        onChange={(value) => update('sponsors_background_images', value)}
                        folder="sponsors-backgrounds"
                      />
                      <SlideshowControls
                        enabled={form.sponsors_slideshow_enabled}
                        onEnabled={(value) => update('sponsors_slideshow_enabled', value)}
                        interval={form.sponsors_slideshow_interval}
                        onInterval={(value) => update('sponsors_slideshow_interval', value)}
                        transition={form.sponsors_transition_duration}
                        onTransition={(value) => update('sponsors_transition_duration', value)}
                      />
                      <section className="grid gap-4 md:grid-cols-2">
                        <RangeControl
                          label="Sponsors dark overlay"
                          value={form.sponsors_overlay_opacity}
                          min={0}
                          max={90}
                          suffix="%"
                          onChange={(value) => update('sponsors_overlay_opacity', value)}
                        />
                        <PositionSelect label="Background position" value={form.sponsors_background_position} onChange={(value) => update('sponsors_background_position', value)} />
                      </section>
                    </>
                  )}
                </>
              )}
            </div>

            <div className="sticky bottom-0 flex items-center justify-end gap-3 border-t border-slate-200 bg-white/95 px-6 py-4 backdrop-blur md:px-8">
              <button type="button" onClick={() => setOpen(false)} className="rounded-xl px-4 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-100">
                Κλείσιμο
              </button>
              <button
                type="button"
                onClick={save}
                disabled={loading || saving || !configId}
                className="flex items-center gap-2 rounded-xl bg-blue-700 px-5 py-2.5 text-sm font-black text-white shadow-lg shadow-blue-700/20 hover:bg-blue-800 disabled:opacity-50"
              >
                <SafeIcon icon={FiSave} className="h-4 w-4" />
                {saving ? 'Αποθήκευση...' : 'Αποθήκευση Backgrounds'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default SiteSettingsBackgrounds